import { useState, type FormEvent } from "react";
import { Button, TextField } from "../ui";
import { useLabel } from "../lib/labels";
import type { FormFieldDef } from "../types";

interface FormShellProps {
  title: string;
  subtitle?: string;
  fields: FormFieldDef[];
  submitLabel: string;
  initial?: Record<string, string>;
  footer?: React.ReactNode;
  serverErrors?: Record<string, string>;
  submitting?: boolean;
  onServerFieldEdit?: (fieldId: string) => void;
  onSubmit: (values: Record<string, string>) => void | Promise<void>;
}

export function FormShell({
  title,
  subtitle,
  fields,
  submitLabel,
  initial,
  footer,
  serverErrors,
  submitting = false,
  onServerFieldEdit,
  onSubmit,
}: FormShellProps) {
  const isRequiredTpl = useLabel("isRequired", "{label} is required.");
  const [values, setValues] = useState<Record<string, string>>(() => {
    const seed: Record<string, string> = {};
    for (const f of fields) {
      seed[f.id] = (initial && initial[f.id]) || f.value || "";
    }
    return seed;
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (submitting) {
      return;
    }
    const next: Record<string, string> = {};
    for (const f of fields) {
      if (f.required && !(values[f.id] || "").trim()) {
        next[f.id] = isRequiredTpl.replace("{label}", f.label);
      }
    }
    setErrors(next);
    if (Object.keys(next).length > 0) {
      return;
    }
    void Promise.resolve(onSubmit(values)).catch((err) => {
      console.warn("[FormShell] onSubmit failed", err);
    });
  };

  const handleChange = (id: string, value: string) => {
    setValues((v) => ({ ...v, [id]: value }));
    setErrors((p) => (p[id] ? { ...p, [id]: "" } : p));
    onServerFieldEdit?.(id);
  };

  const fieldMessage = (fieldId: string): string | undefined => {
    const local = errors[fieldId];
    if (local) {
      return local;
    }
    const server = serverErrors?.[fieldId];
    return server || undefined;
  };

  return (
    <form onSubmit={handleSubmit} className="ac-form" autoComplete="off">
      <header className="ac-form__head">
        <h2 className="ac-form__title">{title}</h2>
        {subtitle && <p className="ac-form__subtitle">{subtitle}</p>}
      </header>

      <div className="ac-form__grid">
        {fields.map((f) => {
          const wide = f.wide || f.type === "textarea";
          const spanCls = wide ? "ac-form__field--full" : "ac-form__field--half";
          const message = fieldMessage(f.id);

          if (f.type === "textarea") {
            return (
              <label key={f.id} className={`ac-form__field ${spanCls}`}>
                <span className="ac-form__field-label">
                  {f.label}
                  {f.required && <span className="ac-label__required">*</span>}
                </span>
                <textarea
                  rows={3}
                  className="ac-textarea ac-form__field-input"
                  placeholder={f.placeholder || ""}
                  value={values[f.id] || ""}
                  onChange={(e) => handleChange(f.id, e.target.value)}
                />
                {message && <p className="ac-form-error">{message}</p>}
              </label>
            );
          }

          return (
            <TextField
              key={f.id}
              className={spanCls}
              label={f.label}
              required={f.required}
              type={f.type || "text"}
              placeholder={f.placeholder || ""}
              value={values[f.id] || ""}
              status={message ? "error" : "default"}
              assistiveText={message}
              onChange={(e) => handleChange(f.id, e.target.value)}
            />
          );
        })}
      </div>

      {footer}

      <Button
        type="submit"
        variant="primary"
        size="sm"
        fullWidth
        disabled={submitting}
      >
        {submitLabel}
      </Button>
    </form>
  );
}
